import { getAppEnvironment } from './appEnvironment'
import { Sentry } from './sentry'

let lastRefreshCaptureMs = 0
let lastUnauthorizedCaptureMs = 0
let visibilityWakeActiveUntil = 0

const AUTH_DEDUPE_MS = 5 * 60 * 1000
const VISIBILITY_WAKE_WINDOW_MS = 15 * 1000

export type AuthSentryContext = 'visibility_wake' | 'refresh_token'

export function beginVisibilityWakeRecovery(): void {
  visibilityWakeActiveUntil = Date.now() + VISIBILITY_WAKE_WINDOW_MS
}

export function isVisibilityWakeRecoveryActive(): boolean {
  return Date.now() < visibilityWakeActiveUntil
}

export function isAuthHttpUnauthorized(status: number | undefined): boolean {
  return status === 401
}

export function isAuthUnauthorizedError(
  error: { status?: number; code?: string; message?: string } | null | undefined,
): boolean {
  if (!error) return false
  if (error.status === 401) return true
  const code = error.code ?? ''
  if (code === 'PGRST301' || code === 'PGRST303') return true
  const message = error.message ?? ''
  return /jwt|unauthorized|invalid.*token/i.test(message)
}

function shouldCaptureAuthEvent(): boolean {
  const env = getAppEnvironment()
  return env === 'staging' || env === 'production'
}

export function captureAuthRefreshTokenFailure(
  status: number,
  detail?: string,
): void {
  if (!shouldCaptureAuthEvent()) {
    return
  }
  const now = Date.now()
  if (now - lastRefreshCaptureMs < AUTH_DEDUPE_MS) {
    return
  }
  lastRefreshCaptureMs = now

  Sentry.captureMessage('Supabase refresh_token request failed', {
    level: 'error',
    tags: {
      area: 'auth',
      context: 'refresh_token',
      http_status: String(status),
    },
    extra: {
      detail: detail?.slice(0, 500),
    },
  })
}

export function captureAuthUnauthorizedFailure(
  context: AuthSentryContext,
  status: number,
  detail?: string,
  url?: string,
): void {
  if (!shouldCaptureAuthEvent()) {
    return
  }
  const now = Date.now()
  if (now - lastUnauthorizedCaptureMs < AUTH_DEDUPE_MS) {
    return
  }
  lastUnauthorizedCaptureMs = now

  Sentry.captureMessage('Supabase API returned unauthorized after tab wake', {
    level: 'error',
    tags: {
      area: 'auth',
      context,
      http_status: String(status),
    },
    extra: {
      detail: detail?.slice(0, 500),
      url: url?.slice(0, 500),
    },
  })
}

/** Report 400 refresh_token responses from GoTrue auto-refresh (not thrown as exceptions). */
export function createAuthSentryFetch(baseFetch: typeof fetch = fetch): typeof fetch {
  return async (input, init) => {
    const response = await baseFetch(input, init)
    try {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : input.url

      if (
        response.status === 400 &&
        url.includes('/auth/v1/token') &&
        url.includes('grant_type=refresh_token')
      ) {
        let body = ''
        try {
          body = await response.clone().text()
        } catch {
          // ignore
        }
        captureAuthRefreshTokenFailure(400, body)
      }

      if (
        response.status === 401 &&
        isVisibilityWakeRecoveryActive() &&
        (url.includes('/rest/v1/') || url.includes('/auth/v1/'))
      ) {
        let body = ''
        try {
          body = await response.clone().text()
        } catch {
          // ignore
        }
        captureAuthUnauthorizedFailure('visibility_wake', 401, body, url)
      }
    } catch {
      // ignore URL parse issues
    }
    return response
  }
}

/** Test helper */
export function resetAuthSentryReportingForTests(): void {
  lastRefreshCaptureMs = 0
  lastUnauthorizedCaptureMs = 0
  visibilityWakeActiveUntil = 0
}
